import { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  GlobalOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Alert, Button, Col, Empty, Input, InputNumber, List, Row, Space, Typography } from 'antd';
import type { DoohBroadcastResponse, DoohMedia, SetDoohBroadcastBody } from '@goelectric/shared-types';
import { MediaKindBadge } from './DoohBadges';
import { formatDuration, formatItemCount, formatResolution } from '../utils/format';

export interface BroadcastComposerProps {
  /** Biblioteca de medios disponibles (de `GET /dooh/media`). */
  media: DoohMedia[];
  /** Emisión activa actual (o `null` si no hay). */
  broadcast: DoohBroadcastResponse | null;
  publishing: boolean;
  /** Habilita publicar; solo super admin / marketing. */
  canPublish: boolean;
  onPublish: (body: SetDoohBroadcastBody) => Promise<void>;
}

interface DraftItem {
  /** Clave estable para el listado (mediaId + posición inicial). */
  key: string;
  mediaId: string;
  filename: string;
  kind: DoohMedia['kind'];
  durationSeconds: number;
}

function toDraft(broadcast: DoohBroadcastResponse | null): DraftItem[] {
  if (!broadcast) return [];
  return broadcast.items.map((item) => ({
    key: `${item.mediaId}-${item.position}`,
    mediaId: item.mediaId,
    filename: item.filename,
    kind: item.kind,
    durationSeconds: item.durationSeconds,
  }));
}

/**
 * Compositor de la emisión global (TG-59): elige medios de la biblioteca, los
 * ordena y define la duración por ítem, y publica la playlist (`PUT /dooh/broadcast`).
 *
 * Recuerda explícitamente que la emisión es **global para toda la red**: un único
 * loop SMIL para todas las pantallas (no hay programación por pantalla).
 */
export function BroadcastComposer({
  media,
  broadcast,
  publishing,
  canPublish,
  onPublish,
}: BroadcastComposerProps) {
  const [draft, setDraft] = useState<DraftItem[]>(() => toDraft(broadcast));
  const [name, setName] = useState(broadcast?.name ?? 'Emisión global');

  // Resincroniza el borrador cuando cambia la emisión cargada.
  useEffect(() => {
    setDraft(toDraft(broadcast));
    setName(broadcast?.name ?? 'Emisión global');
  }, [broadcast]);

  const selectedIds = useMemo(() => new Set(draft.map((item) => item.mediaId)), [draft]);
  const totalSeconds = useMemo(
    () => draft.reduce((sum, item) => sum + (item.durationSeconds || 0), 0),
    [draft],
  );

  const add = (item: DoohMedia) => {
    setDraft((current) => [
      ...current,
      {
        key: `${item.id}-${current.length}`,
        mediaId: item.id,
        filename: item.filename,
        kind: item.kind,
        durationSeconds: item.durationSeconds,
      },
    ]);
  };

  const remove = (key: string) => setDraft((current) => current.filter((item) => item.key !== key));

  const move = (index: number, direction: -1 | 1) => {
    setDraft((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      if (!moved) return current;
      next.splice(target, 0, moved);
      return next;
    });
  };

  const setDuration = (key: string, seconds: number | null) => {
    setDraft((current) =>
      current.map((item) =>
        item.key === key ? { ...item, durationSeconds: seconds ?? 0 } : item,
      ),
    );
  };

  const publish = () => {
    const body: SetDoohBroadcastBody = {
      name: name.trim() || undefined,
      items: draft.map((item) => ({
        mediaId: item.mediaId,
        durationSeconds: item.durationSeconds > 0 ? item.durationSeconds : undefined,
      })),
    };
    void onPublish(body);
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        icon={<GlobalOutlined />}
        message="La emisión es global para toda la red"
        description="Se publica una única playlist para todas las pantallas; no se programa contenido por pantalla. El cambio se aplica en la próxima actualización de las pantallas."
      />

      <Input
        addonBefore="Nombre"
        value={name}
        onChange={(event) => setName(event.target.value)}
        maxLength={120}
        aria-label="Nombre de la emisión"
        style={{ maxWidth: 420 }}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={10}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Biblioteca de medios
          </Typography.Title>
          {media.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Sube imágenes o videos a la biblioteca para poder emitirlos."
            />
          ) : (
            <List
              size="small"
              bordered
              dataSource={media}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button
                      key="add"
                      size="small"
                      icon={<PlusOutlined />}
                      disabled={!canPublish || selectedIds.has(item.id)}
                      onClick={() => add(item)}
                      aria-label={`Agregar ${item.filename}`}
                    >
                      Agregar
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={item.filename}
                    description={
                      <Space size={6} wrap>
                        <MediaKindBadge kind={item.kind} />
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {formatResolution(item.width, item.height)} · {formatDuration(item.durationSeconds)}
                        </Typography.Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Col>

        <Col xs={24} lg={14}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Playlist de la emisión
          </Typography.Title>
          {draft.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Agrega medios de la biblioteca para crear la emisión global."
            />
          ) : (
            <List
              size="small"
              bordered
              dataSource={draft}
              renderItem={(item, index) => (
                <List.Item
                  actions={[
                    <Button
                      key="up"
                      size="small"
                      type="text"
                      icon={<ArrowUpOutlined />}
                      disabled={index === 0 || !canPublish}
                      onClick={() => move(index, -1)}
                      aria-label={`Subir ${item.filename}`}
                    />,
                    <Button
                      key="down"
                      size="small"
                      type="text"
                      icon={<ArrowDownOutlined />}
                      disabled={index === draft.length - 1 || !canPublish}
                      onClick={() => move(index, 1)}
                      aria-label={`Bajar ${item.filename}`}
                    />,
                    <Button
                      key="remove"
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={!canPublish}
                      onClick={() => remove(item.key)}
                      aria-label={`Quitar ${item.filename}`}
                    />,
                  ]}
                >
                  <Space size="middle" style={{ width: '100%' }} align="center">
                    <Typography.Text style={{ fontFamily: 'var(--ge-font-mono)', minWidth: 24 }}>
                      {index + 1}.
                    </Typography.Text>
                    <Typography.Text ellipsis style={{ maxWidth: 260 }}>
                      {item.filename}
                    </Typography.Text>
                    <InputNumber
                      min={1}
                      max={3600}
                      value={item.durationSeconds}
                      disabled={!canPublish}
                      onChange={(value) => setDuration(item.key, value)}
                      addonAfter="s"
                      aria-label={`Duración de ${item.filename}`}
                      style={{ width: 140 }}
                    />
                  </Space>
                </List.Item>
              )}
            />
          )}

          <Space style={{ marginTop: 12, width: '100%', justifyContent: 'space-between' }} wrap>
            <Typography.Text type="secondary">
              {formatItemCount(draft.length)} · duración total {formatDuration(totalSeconds)}
            </Typography.Text>
            <Button
              type="primary"
              loading={publishing}
              disabled={draft.length === 0 || !canPublish}
              onClick={publish}
            >
              Publicar emisión
            </Button>
          </Space>
        </Col>
      </Row>
    </Space>
  );
}
